import ActionFeedbackModal from '@/components/ActionFeedbackModal';
import ConfirmDialog from '@/components/ConfirmDialog';
import Constants from '@/Constants';
import Category from '@/models/Category';
import { CourseLessonContentTO } from '@/models/CourseLessonContentTO';
import Tag from '@/models/Tag';
import { analyticsService, PlaylistEvents } from '@/services/AnalyticsService';
import { ApplicationState } from '@/store';
import { createCourseRequest, createCourseReset } from '@/store/list/action';
import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import MyListForm from '../MyListForm';
import './style.scss';

const CreateCoursePage: React.FC = () => {
    const history = useHistory();
    const dispatch = useDispatch();

    const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false);

    const { strings, organizationId, author, ownerId, hasError, loading, successOnCreateCourse, myCourse } = useSelector(
        (state: ApplicationState) => ({
            strings: state.locale.data,
            organizationId: state.identity.data.user && state.identity.data.user.orgId,
            author: `${state.identity.data.person.firstName} ${state.identity.data.person.lastName}`,
            ownerId: state.identity.data.person.id,
            hasError: state.lists.hasError,
            loading: state.lists.loading,
            successOnCreateCourse: state.lists.successOnCreateCourse,
            myCourse: state.lists.courseData,
        }),
    );

    const draftCourse: CourseLessonContentTO = {
        name: '',
        description: '',
        locale: strings._LANGUAGE,
        applicationId: Constants.APP_ID,
        organizationId,
        moduleId: Constants.PLAYLIST_MODULE_ID,
        author,
        ownerId,
        categories: [] as Category[],
        tags: [] as Tag[],
        type: 'REGULAR',
    } as CourseLessonContentTO;

    const [course, setCourse] = useState<CourseLessonContentTO>(draftCourse);
    const [isFormValid, setIsFormValid] = useState<boolean>(true);

    const myContentIdToRedirectBack = Number(localStorage.getItem(Constants.MY_CONTENT_ID_TO_REDIRECT_BACK));

    const handleCreateNewList = (event: SyntheticEvent) => {
        event.preventDefault();
        dispatch(
            createCourseRequest({
                ...course,
                bizId: `${course.name.substring(0, 85)}_${new Date().getTime().toString()}`,
                contentId: myContentIdToRedirectBack || null,
            }),
        );
        setShowFeedbackModal(true);
    };

    const closeModalHandler = () => {
        setShowFeedbackModal(false);
        history.push('/mine/mycreations');
    };

    const onYesHandler = () => {
        if (myContentIdToRedirectBack) {
            history.goBack();
        } else {
            history.replace(`/mine/courses/${myCourse.id}/suggestedContents`);
        }
    };

    useEffect(() => {
        return () => {
            dispatch(createCourseReset());
            localStorage.removeItem(Constants.MY_CONTENT_ID_TO_REDIRECT_BACK);
        };
    }, []);

    useEffect(() => {
        if (successOnCreateCourse) {
            analyticsService.logEvent(PlaylistEvents.CREATE_STUDENT_PLAYLIST);
        }
    }, [successOnCreateCourse]);

    const getConfirmDialogContent = () => {
        return <span dangerouslySetInnerHTML={{ __html: strings.TEXT_ACCESS_MY_CREATIONS }} />;
    };

    return (
        <>
            {showFeedbackModal && !successOnCreateCourse && (
                <ActionFeedbackModal
                    error={hasError}
                    errorMessage={strings.ERROR_WHILE_SAVE_DATA}
                    loading={loading}
                    onRetry={handleCreateNewList}
                    success={false}
                    successMessage=""
                />
            )}

            {myContentIdToRedirectBack ? (
                <ConfirmDialog
                    color="#3f9b35"
                    content={getConfirmDialogContent()}
                    customClassName="create-mylist-feedback-modal"
                    icon={Constants.icons.CHECK_CIRCLE}
                    rightButtonTitle={strings.I_GOT_IT}
                    rightClick={onYesHandler}
                    show={successOnCreateCourse}
                    title={strings.LIST_CREATED}
                />
            ) : (
                <ConfirmDialog
                    color="#3f9b35"
                    content={strings.WOULD_YOU_LIKE_TO_ADD_CONTENTS_NOW}
                    customClassName="create-mylist-feedback-modal"
                    icon={Constants.icons.CHECK_CIRCLE}
                    leftButtonTitle={strings.NO}
                    leftClick={closeModalHandler}
                    rightButtonTitle={strings.YES}
                    rightClick={onYesHandler}
                    show={successOnCreateCourse}
                    title={strings.LIST_CREATED_SUCCESSFULLY}
                />
            )}

            <div className="mylist">
                <div className="create-mylist">
                    <MyListForm course={course} setCourse={setCourse} setIsFormValid={setIsFormValid} title={strings.NEW_LIST} />

                    <div className={`send-button-container ${isFormValid ? '' : 'disabled'}`}>
                        <button className="btn btn-create" disabled={!isFormValid} onClick={(e) => handleCreateNewList(e)} type="button">
                            {myContentIdToRedirectBack ? strings.CONCLUDE : strings.ADVANCE}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};
export default CreateCoursePage;
